<template>
  <div class="workspace-layout" :class="{ collapsed: appStore.sidebarCollapsed }">
    <aside class="workspace-sidebar">
      <div class="sidebar-brand"><BrandLogo :show-tagline="!appStore.sidebarCollapsed" /></div>
      <nav class="sidebar-nav" :aria-label="t('app.common.workspace')">
        <RouterLink
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          class="sidebar-link"
          :class="{ 'is-active': isNavActive(item.routes) }"
          :title="item.label"
        >
          <UIcon :name="item.icon" />
          <span>{{ item.label }}</span>
        </RouterLink>
      </nav>
      <div class="sidebar-bottom">
        <a :href="siteUrl" class="sidebar-link" target="_blank" rel="noopener">
          <UIcon name="i-lucide-globe-2" /><span>www.cnfindbao.com</span>
        </a>
        <button class="collapse-button" type="button" @click="appStore.toggleSidebar">
          <UIcon
            :name="
              appStore.sidebarCollapsed ? 'i-lucide-panel-left-open' : 'i-lucide-panel-left-close'
            "
          />
          <span>{{ appStore.sidebarCollapsed ? '' : t('app.common.close') }}</span>
        </button>
      </div>
    </aside>

    <header class="workspace-topbar">
      <div class="mobile-brand"><BrandLogo :show-tagline="false" /></div>
      <UButton
        class="mobile-menu-button"
        color="neutral"
        variant="ghost"
        icon="i-lucide-menu"
        @click="appStore.mobileMenuOpen = true"
      />
      <div class="topbar-spacer" />
      <LocaleSwitcher />
      <ThemeSwitcher class="desktop-theme" />
      <div class="user-menu">
        <UserMenu />
        <UButton
          color="neutral"
          variant="ghost"
          icon="i-lucide-log-out"
          :aria-label="t('app.common.signOut')"
          class="cursor-pointer"
          @click="handleSignOut"
        />
      </div>
    </header>

    <main class="workspace-main"><RouterView /></main>

    <Transition name="drawer-fade">
      <div
        v-if="appStore.mobileMenuOpen"
        class="mobile-drawer-backdrop"
        @click.self="appStore.mobileMenuOpen = false"
      >
        <aside class="mobile-drawer">
          <div class="mobile-drawer-head">
            <BrandLogo /><UButton
              color="neutral"
              variant="ghost"
              icon="i-lucide-x"
              @click="appStore.mobileMenuOpen = false"
            />
          </div>
          <nav class="sidebar-nav">
            <RouterLink
              v-for="item in navItems"
              :key="item.to"
              :to="item.to"
              class="sidebar-link"
              :class="{ 'is-active': isNavActive(item.routes) }"
              @click="appStore.mobileMenuOpen = false"
            >
              <UIcon :name="item.icon" /><span>{{ item.label }}</span>
            </RouterLink>
          </nav>
          <div class="drawer-tools">
            <ThemeSwitcher placement="up" /><a :href="siteUrl" target="_blank"
              >www.cnfindbao.com ↗</a
            >
          </div>
        </aside>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import BrandLogo from '@/components/BrandLogo.vue'
import LocaleSwitcher from '@/components/LocaleSwitcher.vue'
import ThemeSwitcher from '@/components/ThemeSwitcher.vue'
import UserMenu from '@/components/UserMenu.vue'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const appStore = useAppStore()
const authStore = useAuthStore()
const siteUrl = import.meta.env.VITE_APP_HOME_DOMAIN
const navItems = computed(() => [
  {
    to: '/',
    routes: ['search', 'search-results'],
    label: t('app.nav.search'),
    icon: 'i-lucide-scan-search',
  },
  { to: '/plans', routes: ['plans'], label: t('app.nav.plans'), icon: 'i-lucide-credit-card' },
  { to: '/history', routes: ['history'], label: t('app.nav.history'), icon: 'i-lucide-history' },
  {
    to: '/commissions',
    routes: ['commissions'],
    label: t('app.nav.commissions'),
    icon: 'i-lucide-handshake',
  },
  {
    to: '/purchases',
    routes: ['purchases'],
    label: t('app.nav.purchases'),
    icon: 'i-lucide-clipboard-list',
  },
  { to: '/favorites', routes: ['favorites'], label: t('app.nav.favorites'), icon: 'i-lucide-star' },
  {
    to: '/orders',
    routes: ['orders', 'order-pay'],
    label: t('app.nav.orders'),
    icon: 'i-lucide-receipt-text',
  },
  {
    to: '/feedback',
    routes: ['feedback'],
    label: t('app.nav.feedback'),
    icon: 'i-lucide-message-square-text',
  },
])
const isNavActive = (names: string[]) => names.includes(String(route.name || ''))

const handleSignOut = async () => {
  await authStore.signOut()
  await router.replace('/login')
}
const handleExpired = () =>
  router.replace({ path: '/login', query: { redirect: router.currentRoute.value.fullPath } })
onMounted(() => window.addEventListener('findbao:auth-expired', handleExpired))
onBeforeUnmount(() => window.removeEventListener('findbao:auth-expired', handleExpired))
</script>

<style scoped>
.workspace-layout {
  min-height: 100vh;
  padding-inline-start: var(--fb-sidebar-width);
  transition: padding 0.22s ease;
}
.workspace-layout.collapsed {
  --fb-sidebar-width: 82px;
}
.workspace-sidebar {
  position: fixed;
  inset-block: 0;
  inset-inline-start: 0;
  z-index: 60;
  width: var(--fb-sidebar-width);
  padding: 18px 13px 14px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background:
    radial-gradient(
      circle at 20% -10%,
      color-mix(in srgb, var(--fb-coral) 18%, transparent),
      transparent 34%
    ),
    var(--fb-sidebar);
  color: var(--fb-sidebar-text);
  border-inline-end: 1px solid var(--fb-sidebar-border);
  transition:
    width 0.22s ease,
    background 0.22s ease;
}
.sidebar-brand {
  min-height: 56px;
  padding: 3px 9px 17px;
  overflow: hidden;
  color: var(--fb-sidebar-text);
}
.sidebar-brand :deep(.brand-copy small) {
  color: var(--fb-sidebar-muted);
}
.sidebar-nav {
  width: 100%;
  min-width: 0;
  min-height: 0;
  display: grid;
  align-content: start;
  gap: 5px;
  overflow-x: hidden;
  overflow-y: auto;
}
.sidebar-link {
  width: 100%;
  min-width: 0;
  min-height: 45px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  overflow: hidden;
  border-radius: 10px;
  color: var(--fb-sidebar-muted);
  font-size: 13px;
  font-weight: 650;
  white-space: nowrap;
  transition:
    color 0.18s,
    background 0.18s;
}
.sidebar-link:hover {
  color: var(--fb-sidebar-text);
  background: var(--fb-sidebar-hover);
}
.sidebar-link.is-active {
  color: var(--fb-sidebar-active-text);
  background: linear-gradient(
    110deg,
    var(--fb-coral),
    color-mix(in srgb, var(--fb-coral) 72%, #a33122)
  );
  box-shadow: 0 10px 26px rgba(243, 100, 70, 0.22);
}
.sidebar-link :deep(svg) {
  flex: none;
  width: 19px;
  height: 19px;
}
.collapsed .sidebar-link span,
.collapsed .collapse-button span {
  display: none;
}
.collapsed .sidebar-link {
  justify-content: center;
  padding: 0;
}
.sidebar-bottom {
  width: 100%;
  min-width: 0;
  margin-top: auto;
  padding-top: 12px;
  display: grid;
  gap: 7px;
  overflow: hidden;
  border-top: 1px solid var(--fb-sidebar-border);
}
.collapse-button {
  min-height: 42px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--fb-sidebar-muted);
  cursor: pointer;
}
.collapse-button:hover {
  background: var(--fb-sidebar-hover);
  color: var(--fb-sidebar-text);
}
.collapsed .collapse-button {
  justify-content: center;
  padding: 0;
}
.workspace-topbar {
  position: sticky;
  top: 0;
  z-index: 45;
  min-height: 66px;
  padding: 0 clamp(16px, 2.6vw, 38px);
  display: flex;
  align-items: center;
  gap: 9px;
  border-bottom: 1px solid color-mix(in srgb, var(--fb-line) 80%, transparent);
  background: color-mix(in srgb, var(--fb-paper) 90%, transparent);
  backdrop-filter: blur(14px);
}
.topbar-spacer {
  flex: 1;
}
.mobile-brand,
.mobile-menu-button {
  display: none;
}
.user-menu {
  min-width: 0;
  margin-inline-start: 6px;
  padding-inline-start: 11px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-inline-start: 1px solid var(--fb-line);
}
.workspace-main {
  min-width: 0;
}
.mobile-drawer-backdrop {
  position: fixed;
  inset: 0;
  z-index: 90;
  background: rgba(5, 18, 27, 0.58);
  backdrop-filter: blur(4px);
}
.mobile-drawer {
  width: min(86vw, 320px);
  height: 100%;
  padding: 16px 13px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--fb-sidebar);
  color: var(--fb-sidebar-text);
  box-shadow: 30px 0 70px rgba(0, 0, 0, 0.3);
}
.mobile-drawer-head {
  min-height: 56px;
  padding: 0 5px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.mobile-drawer-head :deep(.brand-copy small) {
  color: var(--fb-sidebar-muted);
}
.drawer-tools {
  margin-top: auto;
  padding: 16px 5px 4px;
  display: grid;
  gap: 14px;
  color: var(--fb-sidebar-muted);
  font-size: 12px;
}
.drawer-fade-enter-active,
.drawer-fade-leave-active {
  transition: opacity 0.2s;
}
.drawer-fade-enter-active .mobile-drawer,
.drawer-fade-leave-active .mobile-drawer {
  transition: transform 0.2s;
}
.drawer-fade-enter-from,
.drawer-fade-leave-to {
  opacity: 0;
}
.drawer-fade-enter-from .mobile-drawer,
.drawer-fade-leave-to .mobile-drawer {
  transform: translateX(-100%);
}
html[dir='rtl'] .drawer-fade-enter-from .mobile-drawer,
html[dir='rtl'] .drawer-fade-leave-to .mobile-drawer {
  transform: translateX(100%);
}
@media (max-width: 900px) {
  .workspace-layout,
  .workspace-layout.collapsed {
    padding-inline-start: 0;
  }
  .workspace-sidebar {
    display: none;
  }
  .workspace-topbar {
    min-height: 62px;
  }
  .mobile-brand,
  .mobile-menu-button {
    display: inline-flex;
  }
  .mobile-brand {
    max-width: 150px;
  }
  .mobile-menu-button {
    order: -1;
  }
  .desktop-theme {
    display: none;
  }
}
@media (max-width: 520px) {
  .mobile-brand {
    display: none;
  }
  .workspace-topbar {
    padding: 0 10px;
    gap: 6px;
  }
  .user-menu {
    padding-inline-start: 5px;
  }
}
</style>
